import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Input } from '../../components/ui/input';
import { Search } from 'lucide-react';

export default function AdminInventory() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get('/admin/inventory').then(r => { setProducts(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const filtered = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.farmer_name || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="admin-inventory-page" className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif font-medium">Inventory</h1>
          <p className="text-sm text-muted-foreground mt-1">Global view of all products across farmers</p>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="inventory-search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search products..." className="pl-9 w-64 rounded-full" />
        </div>
      </div>

      <Card className="rounded-xl">
        <CardContent className="pt-0">
          {loading ? (
            <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Farmer</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Total Stock</TableHead>
                  <TableHead>Preorders</TableHead>
                  <TableHead>Remaining</TableHead>
                  <TableHead>Harvest Date</TableHead>
                  <TableHead>Delivery Days</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((p) => (
                  <TableRow key={p.product_id} data-testid={`inventory-row-${p.product_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {p.images?.[0] && <img src={p.images[0]} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                        <div>
                          <p className="font-medium text-sm">{p.name}</p>
                          <p className="text-xs text-muted-foreground">{p.variety}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{p.farmer_name}</TableCell>
                    <TableCell className="text-sm capitalize">{p.category}</TableCell>
                    <TableCell className="font-medium">{p.stock} kg</TableCell>
                    <TableCell>{p.preorders || 0}</TableCell>
                    <TableCell className={`font-medium ${(p.stock - (p.preorders || 0)) < 50 ? 'text-destructive' : ''}`}>{Math.max(0, p.stock - (p.preorders || 0))} kg</TableCell>
                    <TableCell className="text-sm">{p.harvest_date || '-'}</TableCell>
                    <TableCell className="text-sm">
                      {p.expected_delivery_days === 1
                        ? <Badge className="rounded-full text-xs bg-primary/10 text-primary border-primary/20">Next Day</Badge>
                        : `${p.expected_delivery_days || 2} days`}
                    </TableCell>
                    <TableCell>
                      <Badge variant={p.status === 'active' ? 'default' : p.stock < 50 ? 'destructive' : 'secondary'}
                        className="rounded-full text-xs capitalize">
                        {p.stock < 50 ? 'Low Stock' : p.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
